/**
 * src/engine/portfolio.ts — monthly portfolio evolution (§6.3, §6.9).
 *
 * Operates on bucket states (the single-portfolio case is one bucket). Each bucket
 * compounds on its own return and dividend yield; scenario-level contributions and
 * shocks are allocated across buckets (contributions by value share; shocks as a uniform
 * percentage). Forced sales are applied later, in the maintenance-cure step (§6.4).
 */
import type { DividendHandling } from '@/types';
import { aggregatePortfolioValue, type BucketState } from './collateral';

/** monthlyReturn = (1 + annual/100)^(1/12) − 1, guarded against a ≤ −100% input. */
export function monthlyReturnFromAnnual(annualPct: number): number {
  const base = Math.max(0, 1 + annualPct / 100);
  return Math.pow(base, 1 / 12) - 1;
}

export interface GrowthStepInput {
  contributionThisMonth: number;
  shockPctThisMonth: number;
  dividendHandling: DividendHandling;
  /** Per-bucket MONTHLY return overrides (Monte Carlo). Falls back to the deterministic
   *  annual→monthly conversion when absent for a bucket. */
  monthlyReturnOverride?: number[];
}

export interface GrowthStepResult {
  /** Dividend cash withheld to service interest this month ('service_interest' mode). */
  dividendCashForServicing: number;
  totalDividend: number;
}

/**
 * Advance every bucket one month: growth → dividends → contribution → shock (§6.3 order).
 * Mutates `states`. Returns dividend cash for the 'service_interest' mode so the caller
 * can offset interest and reinvest any remainder.
 */
export function advancePortfolio(states: BucketState[], input: GrowthStepInput): GrowthStepResult {
  let dividendCashForServicing = 0;
  let totalDividend = 0;

  for (let i = 0; i < states.length; i++) {
    const b = states[i]!;
    // 1. Growth (Monte Carlo may inject a monthly return; otherwise convert from annual).
    const monthlyReturn = input.monthlyReturnOverride?.[i] ?? monthlyReturnFromAnnual(b.expectedReturnAnnualPct);
    b.value *= 1 + monthlyReturn;
    // 2. Dividends (§6.9).
    const div = b.value * (b.dividendYieldAnnualPct / 100 / 12);
    totalDividend += div;
    if (input.dividendHandling === 'reinvest') {
      b.value += div;
      b.basis += div; // basis added at cost
    } else if (input.dividendHandling === 'service_interest') {
      dividendCashForServicing += div;
    }
    // 'spend' → removed from the portfolio (consumed); nothing added.
  }

  // 3. Contributions, allocated by post-growth value share.
  const total = aggregatePortfolioValue(states);
  const n = states.length;
  for (const b of states) {
    const share = total > 0 ? b.value / total : 1 / n;
    const contrib = input.contributionThisMonth * share;
    b.value += contrib;
    b.basis += contrib;
    // 4. Shock (uniform % market move; does not change basis).
    b.value = Math.max(0, b.value * (1 + input.shockPctThisMonth / 100));
  }

  return { dividendCashForServicing, totalDividend };
}

/** Reinvest cash proportionally across buckets, increasing value and basis at cost. */
export function reinvestCash(states: BucketState[], amount: number): void {
  if (amount <= 0) return;
  const total = aggregatePortfolioValue(states);
  const n = states.length;
  for (const b of states) {
    const share = total > 0 ? b.value / total : 1 / n;
    const add = amount * share;
    b.value += add;
    b.basis += add;
  }
}
